import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '影视作品', to: '/category' },
  { label: '资讯', to: '/news' },
  { label: '演员介绍', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?keyword=${encodeURIComponent(value)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span
              className={`w-9 h-9 flex items-center justify-center rounded-lg ${
                transparent ? 'bg-background-50/20 text-background-50' : 'bg-primary-500 text-background-50'
              }`}
            >
              <i className="ri-film-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading font-bold text-lg tracking-wide ${
                  transparent ? 'text-background-50' : 'text-foreground-950'
                }`}
              >
                夏目彩春
              </span>
              <span
                className={`font-label text-[10px] tracking-[0.2em] uppercase ${
                  transparent ? 'text-background-100/80' : 'text-foreground-500'
                }`}
              >
                Natsume Ayaharu
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                    transparent
                      ? isActive
                        ? 'text-background-50 font-medium bg-background-50/15'
                        : 'text-background-100/90 hover:text-background-50 hover:bg-background-50/10'
                      : isActive
                      ? 'text-primary-600 font-medium bg-primary-50'
                      : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form
              onSubmit={submitSearch}
              className={`flex items-center gap-2 rounded-full pl-3.5 pr-1 py-1 border transition-colors ${
                transparent
                  ? 'bg-background-50/15 border-background-50/25'
                  : 'bg-background-100 border-background-200'
              }`}
            >
              <i className={`ri-search-line text-sm ${transparent ? 'text-background-100/80' : 'text-foreground-500'}`} />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品或资讯"
                className={`w-40 text-sm bg-transparent outline-none ${
                  transparent
                    ? 'text-background-50 placeholder:text-background-100/60'
                    : 'text-foreground-900 placeholder:text-foreground-400'
                }`}
              />
              <button
                type="submit"
                className="px-3 py-1.5 text-xs rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md ${
              transparent ? 'text-background-50' : 'text-foreground-900'
            }`}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-1">
            <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full pl-3.5 pr-1 py-1 mb-2 bg-background-100 border border-background-200">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品或资讯"
                className="flex-1 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="px-3 py-1.5 text-xs rounded-full bg-accent-500 text-background-50 whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm rounded-md ${
                    isActive ? 'text-primary-600 bg-primary-50 font-medium' : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}